/**
 * PSTUDIO-1433 — persistence for the resizable width of the "Expanded Cell View"
 * side panel. Kept as a tiny pure module (no MobX/store imports) so the
 * localStorage + clamp contract can be unit-tested in isolation, mirroring
 * expanded-cell-view-storage.ts (open/closed) and auto-renumber-storage.ts.
 *
 * The width is stored in CSS px. It is clamped to [MIN, MAX] on BOTH read and
 * write (per the react-apps clamp-at-every-boundary rule) so a stale or tampered
 * localStorage value can never drive the panel out of its supported range, and
 * localStorage itself never holds an out-of-range value.
 */
export const EXPANDED_CELL_VIEW_WIDTH_STORAGE_KEY =
  'ps-expanded-cell-view-width';

// Sizing floor/ceiling + default (px). 500 preserves the previous fixed width
// as the default; 320 keeps the JSON editor usable; 900 stops the panel from
// swallowing the whole editor on a wide screen. No spacing token maps to these
// panel-level literals (the scale tops out far below), so they are intentional.
export const EXPANDED_CELL_VIEW_MIN_WIDTH = 320;
export const EXPANDED_CELL_VIEW_MAX_WIDTH = 900;
export const EXPANDED_CELL_VIEW_DEFAULT_WIDTH = 500;

export function clampExpandedCellViewWidth(
  value: number | undefined | null
): number {
  if (value == null || !Number.isFinite(value)) {
    return EXPANDED_CELL_VIEW_DEFAULT_WIDTH;
  }
  return Math.min(
    EXPANDED_CELL_VIEW_MAX_WIDTH,
    Math.max(EXPANDED_CELL_VIEW_MIN_WIDTH, value)
  );
}

export function loadExpandedCellViewWidth(): number {
  try {
    const raw = localStorage.getItem(EXPANDED_CELL_VIEW_WIDTH_STORAGE_KEY);
    if (raw == null) return EXPANDED_CELL_VIEW_DEFAULT_WIDTH;
    const parsed = Number(raw);
    // `Number('not-a-number')` is NaN → clamp falls back to DEFAULT.
    return clampExpandedCellViewWidth(parsed);
  } catch {
    return EXPANDED_CELL_VIEW_DEFAULT_WIDTH;
  }
}

export function persistExpandedCellViewWidth(value: number): void {
  try {
    localStorage.setItem(
      EXPANDED_CELL_VIEW_WIDTH_STORAGE_KEY,
      String(clampExpandedCellViewWidth(value))
    );
  } catch {
    // localStorage unavailable (private mode / denied / SSR / quota) —
    // non-fatal; the in-memory observable still drives this session.
  }
}
